import { Base } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { ApiKeyRow } from './types'

const {
  Button,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogTitle,
  DialogDescription,
  DialogCloseButton,
  DialogHeader,
  DialogFooter,
  Callout
} = Base

interface RevokeKeyDialogProps {
  isOpen: boolean
  apiKey?: ApiKeyRow
  saving: boolean
  onClose: () => void
  onRevoke: () => void
}

// Named after the RevokeAPIKey RPC; the button says Delete because that is what it looks like
// to a merchant — the key is gone from the list and cannot come back.
export const RevokeKeyDialog: React.FC<RevokeKeyDialogProps> = ({
  isOpen,
  apiKey,
  saving,
  onClose,
  onRevoke
}) => {
  const { t } = useTranslation()

  if (!apiKey) return null

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogPopup size="sm">
          <div className="flex flex-col gap-4">
            <DialogCloseButton />
            <DialogHeader>
              <DialogTitle>
                {t('api_keys.delete_dialog.title', { name: apiKey.name })}
              </DialogTitle>
              <DialogDescription>
                {t('api_keys.delete_dialog.description', {
                  group: apiKey.group
                })}
              </DialogDescription>
            </DialogHeader>

            <Callout tone="danger" className="flex flex-col gap-2 text-xs">
              <p className="text-xs">
                {t('api_keys.delete_dialog.warning_restore')}
              </p>
              {/* Nothing records when a key was last used, so the dialog cannot pretend to know. */}
              <p className="text-xs">
                {t('api_keys.delete_dialog.warning_usage')}
              </p>
            </Callout>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('api_keys.delete_dialog.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                loading={saving}
                onClick={onRevoke}
              >
                {t('api_keys.delete_dialog.submit')}
              </Button>
            </DialogFooter>
          </div>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}
